import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Users, Package, CalendarDays, Menu, X,
  BarChart3, PawPrint, BedDouble, FileText, Settings, HelpCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import TutorialModal from './TutorialModal';

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, hasPermission } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  if (isAdmin()) return null;

  const mainTabs = [
    { path: '/panel', icon: Home, label: 'Ana Sayfa', perm: null },
    { path: '/calendar', icon: CalendarDays, label: 'Takvim', perm: 'appointments' },
    { path: '/customers', icon: Users, label: 'Müşteriler', perm: 'customers' },
    { path: '/stock', icon: Package, label: 'Stok', perm: 'stock' },
  ].filter(t => !t.perm || hasPermission(t.perm));

  const menuItems = [
    { path: '/cash', icon: BarChart3, label: 'Raporlar', color: 'text-purple-600', bg: 'bg-purple-50', perm: 'reports' },
    { path: '/pets', icon: PawPrint, label: 'Hastalar', color: 'text-orange-600', bg: 'bg-orange-50', perm: 'customers' },
    { path: '/boarding', icon: BedDouble, label: 'Konaklama', color: 'text-violet-600', bg: 'bg-violet-50', perm: 'boarding' },
    { path: '/records', icon: FileText, label: 'Kayıtlar', color: 'text-sky-600', bg: 'bg-sky-50', perm: 'reports' },
    { path: '/settings', icon: Settings, label: 'Ayarlar', color: 'text-gray-600', bg: 'bg-gray-100', perm: 'settings' },
    { path: 'tutorial', icon: HelpCircle, label: 'Rehber', color: 'text-[#6b6de3]', bg: 'bg-[#6b6de3]/10', perm: null },
  ].filter(m => !m.perm || hasPermission(m.perm));

  const isMenuItemActive = menuItems.some(m => m.path !== 'tutorial' && location.pathname === m.path);

  const handleMenuItemClick = (item) => {
    setMenuOpen(false);
    if (item.path === 'tutorial') {
      setShowTutorial(true);
    } else {
      navigate(item.path);
    }
  };

  return (
    <>
      {/* Menu Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-[9998] lg:hidden" data-testid="menu-overlay">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <div className="absolute bottom-[68px] left-3 right-3 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 safe-area-bottom animate-in slide-in-from-bottom-4 duration-200" data-testid="menu-panel">
            <div className="grid grid-cols-3 gap-3">
              {menuItems.map((item) => {
                const isActive = item.path !== 'tutorial' && location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleMenuItemClick(item)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${
                      isActive ? 'bg-[#6b6de3]/10 ring-1 ring-[#6b6de3]/30' : 'hover:bg-gray-50 active:scale-95'
                    }`}
                    data-testid={`menu-${item.path.replace('/', '') || item.path}`}
                  >
                    <div className={`w-11 h-11 ${item.bg} rounded-xl flex items-center justify-center`}>
                      <item.icon size={22} className={isActive ? 'text-[#6b6de3]' : item.color} />
                    </div>
                    <span className={`text-[11px] font-medium ${isActive ? 'text-[#6b6de3]' : 'text-gray-700'}`}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Nav Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-[9999] glass border-t border-gray-200/50 safe-area-bottom lg:hidden">
        <div className="max-w-lg mx-auto flex justify-around items-center h-16">
          {mainTabs.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => { setMenuOpen(false); navigate(item.path); }}
                className={`flex flex-col items-center justify-center w-full h-full transition-colors ${
                  isActive ? 'text-[#6b6de3]' : 'text-[#8E8E93]'
                }`}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
              >
                <Icon size={24} strokeWidth={isActive ? 2 : 1.5} className="mb-0.5" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}

          {/* Menu Button */}
          <button
            onClick={() => setMenuOpen(prev => !prev)}
            className={`flex flex-col items-center justify-center w-full h-full transition-colors ${
              menuOpen || isMenuItemActive ? 'text-[#6b6de3]' : 'text-[#8E8E93]'
            }`}
            data-testid="nav-menu"
          >
            {menuOpen ? (
              <X size={24} strokeWidth={2} className="mb-0.5" />
            ) : (
              <Menu size={24} strokeWidth={isMenuItemActive ? 2 : 1.5} className="mb-0.5" />
            )}
            <span className="text-[10px] font-medium">Menü</span>
          </button>
        </div>
      </nav>

      {/* Tutorial Modal */}
      {showTutorial && <TutorialModal onClose={() => setShowTutorial(false)} />}
    </>
  );
};

export default BottomNav;
